import { getYear, setMonth } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { InstantSearchServerState } from 'react-instantsearch'
import { NextHead } from 'components/Head/Head'
import { LocaleContext } from 'components/common/LocaleContext'
import { searchIndex } from 'containers/meilisearchClient'
import { useCanonicalUrl } from 'containers/seoPages/useCanonicalUrl'
import { capitalize } from 'helpers'
import { getPagniationLinks } from './getPaginationLinks'
import { useHrefLang } from './useHrefLang'

interface MetaProps {
  typeOrDistanceKey: string | null
  serverState: InstantSearchServerState | undefined
  parsedLocation?: string
  parsedMonth?: number
  nextMonthTS?: number
}

export default function Meta({
  typeOrDistanceKey,
  parsedLocation,
  parsedMonth,
  nextMonthTS,
  serverState
}: MetaProps) {
  const { t } = useTranslation(['home', 'common'])
  const { dateStringLocale } = useContext(LocaleContext)
  const currentYear = getYear(new Date())
  const nextYear = currentYear + 1
  const displayLocation = parsedLocation ? ` ${parsedLocation}` : ''
  const displayTypeOrDistance = typeOrDistanceKey
    ? t(`common:${typeOrDistanceKey}` as never)
    : t('allRaces')
  const displayMonth =
    parsedMonth !== undefined
      ? setMonth(new Date(), parsedMonth).toLocaleDateString(dateStringLocale, {
          month: 'long'
        })
      : ''
  const nextMonthDate = nextMonthTS ? new Date(nextMonthTS * 1000) : 0
  const nextMonthYear = nextMonthDate ? getYear(nextMonthDate) : ''
  const displayPeriod =
    displayMonth.length > 0
      ? `${displayMonth} ${nextMonthYear}`
      : `${currentYear} - ${nextYear}`

  const { canonicalUrl, currentPath } = useCanonicalUrl()
  const hrefLangs = useHrefLang()
  const initialResult = serverState?.initialResults[searchIndex]
  const paginationLinks = getPagniationLinks(initialResult, currentPath)
  const description = `${displayTypeOrDistance} : ${t(
    'completeListOfUpcomingRaces'
  )} ${displayPeriod}. ${t('discoverMilesRepublic')}`

  return (
    <NextHead>
      <title>
        {`${capitalize(displayTypeOrDistance)}${displayLocation} : ${t(
          'calendar'
        )} ${currentYear} - ${nextYear}`}
      </title>
      <meta name="description" content={description} />
      <link rel="icon" href="/favicon.ico" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        property="og:title"
        content={`${displayTypeOrDistance} : ${t('upToDateRaceCalendar')}`}
      />
      <meta
        name="twitter:title"
        content={`${displayTypeOrDistance} : ${t('upToDateRaceCalendar')}`}
      />
      <meta property="og:site_name" content="Miles Republic" />
      <meta name="twitter:site_name" content="Miles Republic" />
      <meta property="og:description" content={description} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content="https://fr.milesrepublic.com/assets/banner.webp"
      />
      <meta
        property="og:image"
        content="https://fr.milesrepublic.com/assets/banner.webp"
      />
      <meta property="fb:app_id" content="429733861348121" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />

      <link rel="canonical" href={canonicalUrl} />
      {...hrefLangs}
      {...paginationLinks}
    </NextHead>
  )
}
